import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { getEnvironmentVariable } from "@utils/shared.environment.utils.ts";
import { protectedResources } from '@lib/auth/authConfig';
import { acquireAccessToken } from '@lib/auth/auth.utils';

/**
 * Shared API client with basic configuration
 */
const apiClient = axios.create({
  baseURL: import.meta.env.DEV
    ? '/api'
    : `${getEnvironmentVariable('VITE_API_ENDPOINT')}/api`,
  timeout: 30000,
});

let msalInstance: PublicClientApplication | null = null;

/**
 * Configure API client with MSAL authentication
 */
export function setupApiAuth(instance: PublicClientApplication): void {
  msalInstance = instance;

  // Request interceptor - add token to requests
  apiClient.interceptors.request.use(
    async (config) => {
      if (!msalInstance) return config;

      // Build full URL to check against protected resources
      const fullUrl = config.baseURL && config.url
        ? `${config.baseURL}${config.url}`
        : config.url || config.baseURL || '';

      const resourceConfig = Object.values(protectedResources).find(resource =>
        fullUrl.includes(resource.endpoint)
      );

      if (resourceConfig) {
        try {
          const token = await acquireAccessToken(msalInstance, resourceConfig.scopes);
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error('Failed to acquire token:', error);
        }
      }

      return config;
    }
  );

  // Response interceptor - handle 401 and retry with fresh token
  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry && msalInstance) {
        originalRequest._retry = true;

        // Build full URL to check against protected resources
        const fullUrl = originalRequest.baseURL && originalRequest.url
          ? `${originalRequest.baseURL}${originalRequest.url}`
          : originalRequest.url || originalRequest.baseURL || '';

        const resourceConfig = Object.values(protectedResources).find(resource =>
          fullUrl.includes(resource.endpoint)
        );

        if (resourceConfig) {
          try {
            const token = await acquireAccessToken(msalInstance, resourceConfig.scopes);
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }

      return Promise.reject(error);
    }
  );
}

export default apiClient;